import { Reducer } from 'redux';
import { IFinanceDocumentState, DocActionTypes } from './types';
import { StudentActionTypes } from '../types';
import { DataActionTypes } from '../../data/types';
import { UIActionTypes } from '../../ui/types';
import { IAnyObj, IDocument, IFinance } from '../../../types';
import getBirthCertRequirements from '../../../components/pages/modals/financeDocs/birthCertificate/getHighlightedErrorSections';
import getEducationSponsorshipRequirements from '../../../components/pages/modals/financeDocs/educationSponsor/getHighlightedErrorSections';
import getOrganisationSponsorshipRequirements from '../../../components/pages/modals/financeDocs/organisationSponsor/getHighlightedErrorSections';
import getParentLetterRequirements from '../../../components/pages/modals/financeDocs/parentLetter/getHighlightedErrorSections';
import getBankLetterRequirements from '../../../components/pages/modals/financeDocs/personalBankLetter/getHighlightedErrorSections';
import getBankStatementRequirements from '../../../components/pages/modals/financeDocs/bankStatement/getHighlightedErrorSections';
import { isLoanTypeUnknown } from '../../../components/organisms/finance/EducationSponsor/utils';
import { transformObject } from '../documents/helpers';
import { getDocDetails } from '../../../utilities/documents';
import deepmerge from 'deepmerge';

const initialState: IFinanceDocumentState = {
  docs: Map<string, IDocument>,
  agentDocumentsRequired: 0,
  agentDocumentsComplete: 0,
  isSectionStarted: false,
  isSectionComplete: false
};

const overwriteMerge = (_: [], sourceArray: []) => sourceArray;

const mainReducer: Reducer<IFinanceDocumentState> = (state = initialState, action) => {
  switch (action.type) {
    case StudentActionTypes.GET_STUDENT_FIN_DOCS_SUCCESS: {
      let financeDocuments = transformObject(action.payload.financeDocuments);

      // BE naming issue evidenceOfIncome => incomeEvidence
      if (financeDocuments.incomeEvidence) {
        financeDocuments = {
          ...financeDocuments,
          evidenceOfIncome: {
            ...financeDocuments.incomeEvidence
          }
        }
        delete financeDocuments.incomeEvidence;
      }
      return {
        ...state,
        ...financeDocuments
      };
    }

    case DocActionTypes.SAVE_METADATA_FOR_NO_DOC_SUCCESS:
    case DocActionTypes.TOGGLE_ACKNOWLEDGE_DOC_SUCCESS: {
      const finDocs = transformObject(action.payload.financeDocuments);

      // merge the delta
      const newState = deepmerge(state, finDocs, {
        arrayMerge: overwriteMerge
      });
      return {
        ...newState
      };
    }
    case StudentActionTypes.SAVE_PERSONAL_INFO_SUCCESS:
    case StudentActionTypes.SAVE_HISTORY_SUCCESS:
    case StudentActionTypes.SAVE_CONSENTS_SUCCESS:
    case StudentActionTypes.SAVE_FINANCE_SUCCESS:
    case StudentActionTypes.SAVE_PRE_CAS_INTERVIEW_SUCCESS:
    case StudentActionTypes.SAVE_VISA_PREP_FORM_SUCCESS:
    case StudentActionTypes.SAVE_DOCUMENTS_SUCCESS:
    case StudentActionTypes.SAVE_FINANCE_DOCUMENTS_SUCCESS:
    case DocActionTypes.ADD_REQUIRED_DOCUMENT_SUCCESS: {
      
      return deepMerge(state, transformObject(action.payload.financeDocuments));
       
    }
    case UIActionTypes.OPEN_FINANCE_UPLOAD_MODAL: {
      const { sponsorType, sponsorKey, docType, docKey, fileNames, metadata } = action.payload;

      if (!metadata) {
        return state;
      }
      const newState = { ...state };
      const newRecord = getDocDetails({ docs: newState, sponsorType, sponsorKey, docType, docKey }) as IAnyObj;

      // extend the object with new metadata, no need to craete new object ref
      Object.entries(metadata).forEach(([key, value]) => {
        if (key === 'isDocumentFixed') {
          // don't update state as fixed until file(s) have been uploaded
          newRecord.isDocumentFixedPending = value;
          newRecord.docCount = fileNames.length;
          if (newRecord.isDocumentFixed) {
            delete newRecord.isDocumentFixed;
          }
        } else if (value !== undefined && key !== 'shouldShowRequirements') {
          newRecord[key] = value;
        }
      });

      return {
        ...newState
      };
    }

    case DataActionTypes.UPLOAD_FILE_SUCCESS: {
      const { sponsorType, sponsorKey, docType, docKey, fileNames, isFinanceDocumentModal } = action.payload;
      const finance = action.payload.finance as IFinance;
      if (!isFinanceDocumentModal) {
        return state;
      }

      const newState = { ...state };
      const newRecord = getDocDetails({ docs: newState, sponsorType, sponsorKey, docType, docKey }) as IAnyObj;

      // strip out file upload failures
      let parsedFileNames: string[] = fileNames;
      parsedFileNames = fileNames?.filter((fName: string) => !fName.includes('upload--error--')) ?? [];
      newRecord.fileNames = parsedFileNames;

      let hasRequirements = false;
      switch (sponsorType) {
        case 'birthCertificate': {
          switch (docType) {
            case 'certificate':
              hasRequirements = getBirthCertRequirements(newRecord).length > 0;
              break;
          }
          break;
        }
        case 'educationSponsorship': {
          // skip requirement checks for Unknown loan types
          const sponsor = finance.educationSponsorship![sponsorKey!];
          hasRequirements = !isLoanTypeUnknown(sponsor.loanType)
            ? getEducationSponsorshipRequirements(newRecord).length > 0
            : false;
          break;
        }
        case 'governmentSponsorship':
        case 'companySponsorship': {
          hasRequirements = getOrganisationSponsorshipRequirements(newRecord).length > 0;
          break;
        }
        case 'familySponsorship': {
          switch (docType) {
            case 'parentLetter': {
              hasRequirements = getParentLetterRequirements(newRecord).length > 0;
              break;
            }
            case 'bankLetter': {
              hasRequirements = getBankLetterRequirements(newRecord).length > 0;
              break;
            }
            case 'bankStatement': {
              hasRequirements = getBankStatementRequirements(newRecord).length > 0;
              break;
            }
          }
        }
      }

      // if birth certificate has requested a translation and hasRequirement
      // only update the status to 'UPLOADED_WITH_ERROR' if the student is required
      // to re-upload another certificate
      if (
        sponsorType === 'birthCertificate' &&
        docType === 'certificate' &&
        newRecord.status === 'REPLACED' &&
        newRecord.isLegalDocument === true &&
        newRecord.hasAllNames === true
      ) {
        return {
          ...newState
        };
      } else {
        newRecord.status = hasRequirements ? 'UPLOADED_WITH_ERROR' : 'UPLOADED_OK';
      }

      return {
        ...newState
      };
    }

    default:
      return state;
  }
};

export { mainReducer as financeDocumentsReducer };


function deepMerge(target, source) {
  for (const key of Object.keys(source)) {
    if (source[key] instanceof Object && key in target) {
      Object.assign(source[key], deepMerge(target[key], source[key]));
    }
  }
  return { ...target, ...source };
}